import { KeyboardEvent, ChangeEvent, ClipboardEvent, Fragment, useRef, ReactNode } from 'react'
import { TextField, Box, styled, TextFieldProps } from '@mui/material'

export type OTPInputProps = Omit<TextFieldProps, 'onChange' | 'value'> & {
  value: string,
  separator?: ReactNode,
  numberOfInputs?: number,
  onChange(value: string): void,
}

const OTPInput = (
  {
    sx = {},
    name,
    separator = <></>,
    numberOfInputs = 6,
    value,
    onChange
  }: OTPInputProps
) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>(new Array(numberOfInputs).fill(null))

  const focusInput = (targetIndex: number) => {
    inputRefs.current[targetIndex]?.focus()
  }

  const selectInput = (targetIndex: number) => {
    inputRefs.current[targetIndex]?.select()
  }

  const _handleOnPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault()

    const pasteData = event.clipboardData.getData('Text').trim()

    if (pasteData.length !== numberOfInputs) {
      return
    }

    onChange(pasteData)
    focusInput(numberOfInputs - 1)
    selectInput(numberOfInputs - 1)
  }

  const _handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, currentIndex: number) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault()
        if (currentIndex > 0) {
          focusInput(currentIndex - 1)
          selectInput(currentIndex - 1)
        }
        break
      case 'ArrowRight':
        event.preventDefault()
        if (currentIndex < numberOfInputs - 1) {
          focusInput(currentIndex + 1)
          selectInput(currentIndex + 1)
        }
        break
      case 'Backspace':
        event.preventDefault()

        onChange(value.slice(0, currentIndex) + value.slice(currentIndex + 1))

        if (currentIndex > 0) {
          focusInput(currentIndex - 1)
          selectInput(currentIndex - 1)
        }
        break
      default:
        break
    }
  }

  const _handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, currentIndex: number) => {
    const currentValue = event.target.value
    const lastValue = currentValue.slice(-1)
    let nextIndex: number = currentIndex
    const otpArray = value.split('')

    otpArray[currentIndex] = lastValue
    onChange(otpArray.join(''))

    if (currentIndex < otpArray.join('').length) {
      nextIndex = currentIndex + 1
    } else if (currentValue && currentIndex < length - 1) {
      nextIndex = otpArray.join('').length
    }

    focusInput(nextIndex)
    selectInput(nextIndex)
  }

  return (
    <Box sx={{
      ...sx,
      display: 'flex',
      gap: 1,
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {Array.from({ length: numberOfInputs }, (_, index) => (
        <Fragment key={index}>
          <StyledTextField
            inputRef={(ele) => (inputRefs.current[index] = ele)}
            name={name}
            inputMode='numeric'
            type='number'
            variant="outlined"
            autoComplete='off'
            size="small"
            inputProps={{
              maxLength: 1,
              'aria-label': `Digit ${index + 1} of OTP`
            }}
            onKeyDown={(event) => _handleKeyDown(event, index)}
            onChange={(event) => _handleChange(event, index)}
            onPaste={_handleOnPaste}
            value={value[index] ?? ''}
          />
          {index < numberOfInputs - 1 && separator}
        </Fragment>
      ))}
    </Box>
  )
}

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    input: {
      padding: '22px 12px',
      width: '20px !important',
      textAlign: 'center',
      caretColor: 'transparent',
      cursor: 'pointer',
      '&::-webkit-outer-spin-button,&::-webkit-inner-spin-button': {
        WebkitAppearance: 'none',
        margin: 0
      },
      '&[type=number]': {
        MozAppearance: 'textfield'
      },
      userSelect: 'none',
      '&::selection': {
        background: 'transparent'
      },
      '&:focus': {
        outline: 'none'
      }
    }
  }
}))

export default OTPInput
